import React, { createContext, useState, useContext, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode'; // A reliable library to decode JWTs

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser && token) {
        // Verify token isn't expired on initial load
        const decodedToken = jwtDecode(token);
        if (decodedToken.exp * 1000 > Date.now()) {
          setUser(JSON.parse(storedUser));
        } else {
          // Token is expired, clear storage
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setUser(null);
          setToken(null);
        }
      }
    } catch (error) {
      // Handle potential JSON parsing errors or invalid tokens
      console.error("Auth context error on initial load:", error);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setUser(null);
      setToken(null);
    }
  }, [token]);

  const login = async (loginIdentifier, password) => {
    const API_URL = 'https://healthcard.searchisend.com/health_api/login.php';

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // This is the corrected part: sending 'loginIdentifier'
      body: JSON.stringify({ loginIdentifier, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to log in.');
    }

    const decodedToken = jwtDecode(data.token);
    const userData = decodedToken.data;

    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', data.token);
    setUser(userData);
    setToken(data.token);

    return userData;
  };

  const checkAuthStatus = async () => {
    if (!token) return;
    try {
      const response = await fetch('https://healthcard.searchisend.com/health_api/get_user_details.php', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(prevUser => ({ ...prevUser, ...userData }));

        const storedUser = JSON.parse(localStorage.getItem('user')) || {};
        localStorage.setItem('user', JSON.stringify({ ...storedUser, ...userData }));
      }
    } catch (e) {
      console.error("Failed to check auth status", e);
    }
  };

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setToken(null);
    // Redirect to login page to clear any protected state
    window.location.href = '/login';
  };

  const authContextValue = {
    isAuthenticated: !!token,
    user,
    token,
    login,
    logout,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

